/**
 * The App-page module loader's URL surface — shared by the shell builder (the
 * importmap), the service worker (the intercept prefix), and the host (the SW
 * script route). Workspace modules resolve to `/workspace/<path>` on the App's
 * own origin; the service worker answers them locally (via the page's guarded
 * HAL), so nothing under this prefix is ever served by the host.
 */

/** Same-origin path prefix the page's `@/` importmap maps to and the service
 *  worker intercepts. Paths under it are workspace-root-relative. */
export const WORKSPACE_ROUTE_PREFIX = "/workspace/";

/** Where the host serves the (generic, static) service-worker script on every
 *  app origin. Root-level so its default scope covers the whole origin. */
export const APP_SW_PATH = "/playground-sw.js";

/** Where a host that ships the page runtime as a static file serves it, for
 *  its shell builder to read back and inline. */
export const PAGE_RUNTIME_PATH = "/playground-page-runtime.js";

/** The page importmap: `@/…` → the workspace route, resolved same-origin. */
export const WORKSPACE_IMPORT_MAP = JSON.stringify({
  imports: { "@/": WORKSPACE_ROUTE_PREFIX },
});

/** The `/workspace/…` URL (origin-relative) for a workspace file path. */
export function workspaceModulePath(path: string): string {
  return WORKSPACE_ROUTE_PREFIX + path.replace(/^\/+/, "");
}

/** A workspace App's program module as the service worker serves it: its
 *  source `module` under `appPath`, compiled to `.js`. */
function programModule(appPath: string, module: string): string {
  return `${appPath}/${module.replace(/\.(tsx|ts|jsx|js)$/i, ".js")}`;
}

/** The `/workspace/…` URL a workspace App's program module loads from. */
export function workspaceProgramPath(appPath: string, module: string): string {
  return workspaceModulePath(programModule(appPath, module));
}

/** The same module as the page importmap's `@/…` specifier. */
export function workspaceProgramSpecifier(
  appPath: string,
  module: string,
): string {
  return `@/${programModule(appPath, module)}`;
}
